import {
  CallableInput,
  CallableToggle,
  CountrySelect,
} from "Components/Shared";
import CallableTextArea from "Components/Shared/CallableTextArea";
import { Field, Formik } from "formik";
import { useAccount, useTicket } from "Hooks/Endpoints";
import React from "react";
import {
  Button,
  Container,
  Dimmer,
  Form,
  Header,
  Icon,
  Loader,
  Menu,
  Segment,
} from "semantic-ui-react";
import * as yup from "yup";

const validation = yup.object().shape({
  customer: yup
    .object()
    .shape({
      id: yup.string().required(),
      name: yup.string().required(),
    })
    .required(),
  contactName: yup.string().required(),
  contactTitle: yup.string().required(),
  areaCode: yup.string().required(),
  qty: yup.number().required().max(100).min(1),
  address: yup.object().shape({
    line1: yup.string().required(),
    line2: yup.string().required(),
    level1: yup.string().required(),
    level2: yup.string().required(),
    postalCode: yup.string().required(),
    country: yup.string().required(),
  }),
});

type AllocationNumberFormProps = {
  onBack: Function;
};

const AllocateNumberForm = ({ onBack }: AllocationNumberFormProps) => {
  const { account } = useAccount();

  const {
    saveAllocation,
    allocationCreatedResult,
    completeAllocation,
    loading,
  } = useTicket();

  const initValues = {
    customer: {
      id: account.id,
      name: account.name,
    },
    countryCode: "gb",
    areaCode: "",
    specialNotes: "",
    qty: 1,
  };

  const buildAddress = (setFieldValue: Function, values: any) => {
    setFieldValue("address.line1", values.line1);
    setFieldValue("address.line2", values.line2);
    setFieldValue("address.level1", values.level1);
    setFieldValue("address.level2", values.level2);
    setFieldValue("address.postalCode", values.postalCode);
    setFieldValue("address.country", values.country);
  };

  const clearAddress = (setFieldValue: Function) => {
    setFieldValue("address.line1", "");
    setFieldValue("address.line2", "");
    setFieldValue("address.level1", "");
    setFieldValue("address.level2", "");
    setFieldValue("address.postalCode", "");
    setFieldValue("address.country", "");
  };

  if (allocationCreatedResult) {
    return (
      <Segment placeholder>
        <Header icon as='h2'>
          <Icon color='green' name='check square outline' />
          <p>
            Allocation Request #{allocationCreatedResult.caseId} has been
            created
          </p>
        </Header>

        <Button.Group>
          <Button
            secondary
            onClick={() => {
              completeAllocation();
              onBack();
            }}
          >
            Close
          </Button>
        </Button.Group>
      </Segment>
    );
  }

  return (
    <Container textAlign='left' fluid>
      <Dimmer active={loading} inverted>
        <Loader inverted />
      </Dimmer>
      <Menu fluid stackable>
        <Menu.Item header onClick={() => onBack()}>
          <Icon name='x' />
        </Menu.Item>
        <Menu.Item header>Number Allocation Request</Menu.Item>
      </Menu>
      <Segment>
        <Formik
          initialValues={initValues}
          onSubmit={(values) => {
            saveAllocation(values);
          }}
          validationSchema={validation}
          render={({
            values,
            handleSubmit,
            setFieldValue,
            validateForm,
            isValid,
          }) => {
            return (
              <Form onSubmit={handleSubmit}>
                <CountrySelect name='countryCode' label='Select Country' />

                <Field
                  name='areaCode'
                  label='Area Code'
                  component={CallableInput}
                />

                <Field
                  name='qty'
                  label='Qty (1-100)'
                  type='number'
                  component={CallableInput}
                  max={100}
                  min={1}
                />

                <Field
                  name='specialNotes'
                  label='Special Notes'
                  placeholder='N/A'
                  component={CallableTextArea}
                />

                <h2>Contact Details</h2>

                <Field
                  name='contactName'
                  label='Contact Name'
                  component={CallableInput}
                />

                <Field
                  name='contactTitle'
                  label='Contact Title'
                  component={CallableInput}
                />

                <h2>Site Details </h2>

                <Field
                  name='sameAsAccount'
                  label='Same As Account Address'
                  component={CallableToggle}
                  onChange={(val: any, data: any) => {
                    if (data.checked) {
                      const { address } = account;
                      buildAddress(setFieldValue, address);
                    } else {
                      clearAddress(setFieldValue);
                    }
                    setTimeout(() => {
                      validateForm();
                    }, 50);
                  }}
                />

                <Field
                  name='address.line1'
                  label='Premises'
                  component={CallableInput}
                />

                <Field
                  name='address.line2'
                  label='Street'
                  component={CallableInput}
                />

                <Field
                  name='address.level2'
                  label='Town'
                  component={CallableInput}
                />

                <Field
                  name='address.level1'
                  label='Province'
                  component={CallableInput}
                />

                <Field
                  name='address.postalCode'
                  label='PostCode'
                  component={CallableInput}
                />

                <Field
                  name='address.country'
                  label='Country'
                  component={CallableInput}
                />

                <Button primary disabled={!isValid}>
                  Submit
                </Button>
              </Form>
            );
          }}
        />
      </Segment>
    </Container>
  );
};

export default AllocateNumberForm;
